import React from 'react';
import { Divider } from '@mui/material';
import ChatRounded from '@mui/icons-material/ChatRounded';
import IosShareRounded from '@mui/icons-material/IosShareRounded';
import { DataFreshness, ToolbarButton } from '../../../ui-kit';
import { useCan } from '../../../ui/PermissionsProvider';
import { useFeature } from '../../../ui/FeaturesProvider';
import ShareDialog from '../../sharing/ShareDialog';
import { useSharedBoard } from '../sharedBoard';

type Freshness = React.ComponentProps<typeof DataFreshness>['status'];

/**
 * The actions in every board's header, in one place so the boards cannot
 * drift apart: when the data was built (and refreshing it), Share, and Chat.
 *
 * On a board opened from a shared link only the "updated" line shows: a
 * viewer cannot refresh from the warehouse, share it on, or use the chat,
 * which can query beyond the board. Share is for admins.
 */
export default function BoardHeaderActions({
  dashboardId, name, view, updatedAt, status, refreshing, onRefresh, loading, chatOpen, onToggleChat, chatTooltip,
}: {
  dashboardId: string;
  /** The board's name, for the Share window. */
  name: string;
  /** Which page of a board with several, e.g. 'campaigns'; shared as that page. */
  view?: string | null;
  updatedAt: string | null | undefined;
  status: Freshness;
  refreshing: boolean;
  onRefresh: () => void;
  loading: boolean;
  chatOpen: boolean;
  onToggleChat: () => void;
  /** "Ask about this report". */
  chatTooltip: string;
}) {
  const { shared } = useSharedBoard();
  // Admins only, and only where the workspace has sharing switched on.
  const sharingOn = useFeature('sharing').enabled;
  const canShare = useCan('manage_workspace') && sharingOn;
  const [sharing, setSharing] = React.useState(false);

  return (
    <>
      <DataFreshness
        updatedAt={updatedAt ?? null}
        status={status}
        refreshing={refreshing}
        onRefresh={shared ? undefined : onRefresh}
        disabled={loading}
        refreshLabel="Refresh data from warehouse"
      />
      {shared ? null : (
        <>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          {canShare ? (
            <ToolbarButton icon={<IosShareRounded />} onClick={() => setSharing(true)} tooltip="Share a read-only link">
              Share
            </ToolbarButton>
          ) : null}
          <ToolbarButton
            icon={<ChatRounded />}
            onClick={onToggleChat}
            selected={chatOpen}
            tooltip={chatOpen ? 'Hide chat' : chatTooltip}
          >
            {chatOpen ? 'Hide chat' : 'Chat'}
          </ToolbarButton>
          {sharing ? (
            <ShareDialog
              open
              onClose={() => setSharing(false)}
              kind="dashboard"
              targetId={dashboardId}
              targetName={name}
              options={view ? { view } : undefined}
            />
          ) : null}
        </>
      )}
    </>
  );
}
